import * as React from "react";
import { Button, type ButtonProps } from "./button";
import { cn } from "../lib/utils";

/**
 * The glow ids, spelled out rather than imported.
 *
 * `@wa/ui` does not depend on `@wa/widgets` (the dependency runs the other way
 * round in spirit: widgets is deliberately kit-free so its output works on a
 * client site), so this union cannot read the catalog in
 * `packages/widgets/src/accentGlows.ts`. It is cross-checked instead: every
 * caller in the platform passes a value typed by that catalog, so an id added
 * there and forgotten here is a compile error, not a silent miss.
 */
export type AccentGlowVariant =
  | "none"
  | "conic"
  | "aurora"
  | "pulse"
  | "spectrum"
  | "breathe"
  | "neon"
  | "orbit"
  | "mist"
  | "flare";

interface AccentButtonProps extends ButtonProps {
  /** Glow effect style. Driven by `theme.accentButtonStyle` setting at
   *  runtime - pass through whatever the storefront layout received from
   *  `/api/public-settings`. */
  glow?: AccentGlowVariant;
  /** Classes for the glow WRAPPER, not the button. Anything that decides the
   *  outer box belongs here - width and flex alignment above all. `className`
   *  reaches the inner `<Button>`, which is one box too deep to size the box
   *  the halo is drawn around. Merged last, so it beats the width this
   *  component works out on its own (tailwind-merge keeps the later width). */
  wrapperClassName?: string;
}

/**
 * Glowing brand-aware button. Wraps the standard `<Button>` with one of
 * nine reactnativeglow-style accent effects, all picking up the active
 * theme's primary + secondary brand colours via CSS vars - so the glow
 * automatically follows whatever the admin sets on the Themes page.
 *
 *   glow="conic"    → animated multi-stop ring cycling through the brand colours
 *   glow="aurora"   → two coloured shadows drifting to opposite sides
 *   glow="pulse"    → heartbeat outer-ring pulse
 *   glow="spectrum" → moving gradient halo behind the button
 *   glow="breathe"  → one soft halo swelling and fading slowly
 *   glow="neon"     → a steady lit line plus bloom, no animation at all
 *   glow="orbit"    → a point of light running around the button's edge
 *   glow="mist"     → a wide soft cloud of colour drifting under the button
 *   glow="flare"    → mostly quiet, one bright flash every few seconds
 *   glow="none"     → plain Button (default)
 *
 * Seven of the nine are a `box-shadow` on the inner control, so nothing
 * can leak outside its rounded corners; only spectrum and orbit need a
 * pseudo-element, and it sits on the wrapper (no inner span) - that works
 * even when AccentButton is nested inside another `<button>` (e.g. an
 * admin demo picker tile), where nested <button>-in-<button> would
 * otherwise cause the browser to reorganise the DOM and break the effect.
 *
 * The stylesheet matches `:is(button, a)`, so the same class pair works on
 * a link - which is what every widget CTA actually renders. See the
 * accent-glow block in packages/ui/src/styles.css.
 */
export const AccentButton = React.forwardRef<HTMLButtonElement, AccentButtonProps>(
  ({ glow = "none", className, wrapperClassName, children, ...props }, ref) => {
    if (glow === "none") {
      return (
        <Button ref={ref} className={className} {...props}>
          {children}
        </Button>
      );
    }
    // Built from the id, not picked out of a ternary chain: the chain ended in
    // a default, so a glow the chain did not name came out wearing pulse - a
    // real look, which is exactly why nobody would have noticed.
    const rootClass = `accent-glow-root accent-glow-${glow}`;
    // The wrapper has to be exactly as wide as the button, because the spectrum
    // halo is an `::after` on the WRAPPER at `inset:-8px`. Left to itself the
    // wrapper is not: `display:inline-flex` still leaves `width:auto`, and a
    // flex item with an auto width STRETCHES to its container under the default
    // `align-items:stretch`. Inside a form that lays its controls out in a
    // column that made the halo a blurred band across the whole form while the
    // button sat narrow at its left edge - measured, and the reason this line
    // exists. Sizing the button alone cannot fix it from one box further in.
    //
    // So the wrapper MIRRORS the button's width intent: a caller that asks for
    // a full-width button (every login and account form does) gets a full-width
    // wrapper, and everyone else gets one that hugs its button. Callers with a
    // width the class string cannot express pass `wrapperClassName`.
    const fullWidth = ` ${className ?? ""} `.includes(" w-full ");
    // The glow classes stay OUT of `cn`: tailwind-merge reads `accent-glow-root`
    // and `accent-glow-<id>` as two accent-color utilities and keeps only the
    // last, so the wrapper lost `accent-glow-root` (its inline-flex box) and the
    // halo wrapped a text line instead of the button.
    return (
      <span className={`${rootClass} ${cn(fullWidth ? "w-full" : "w-fit", wrapperClassName)}`}>
        <Button ref={ref} className={className} {...props}>
          {children}
        </Button>
      </span>
    );
  },
);
AccentButton.displayName = "AccentButton";
